import {
  PaymentAgent,
  PaymentAgentParams,
  PaymentProvider,
  SubscriptionType,
} from './PaymentProvider';
import { PaymentApi, VoucherType } from '../API/PaymentApi';
import UserApi from '../API/UserApi';

const VOUCHER_TYPES_BY_SUBSCRIPTION_TYPE = {
  [SubscriptionType.Monthly]: VoucherType.ONE_MONTH_SUBSCRIPTION,
  [SubscriptionType.Annual]: VoucherType.YEARLY_SUBSCRIPTION,
};

export class CoachBotPaymentProvider implements PaymentProvider, PaymentAgent {
  private onPaymentApproved: (
    subscriptionType: SubscriptionType,
    metadata?: Record<string, any>,
  ) => void;
  private onPaymentVerified: (
    subscriptionType: SubscriptionType,
    metadata?: Record<string, any>,
  ) => void;
  private onPaymentFinished: (
    subscriptionType: SubscriptionType,
    metadata?: Record<string, any>,
  ) => void;
  private onError: (error: Error) => void;

  getId() {
    return 'coachbot';
  }
  async createAgent({
    userId,
    url,
    onReady,
    onPaymentApproved,
    onPaymentVerified,
    onPaymentFinalized,
    onError,
  }: PaymentAgentParams): Promise<PaymentAgent> {
    console.log('Initializing CoachBot Payment Provider');
    // Initialize the payment provider

    this.onPaymentApproved = onPaymentApproved;
    this.onPaymentVerified = onPaymentVerified;
    this.onPaymentFinished = onPaymentFinalized;
    this.onError = onError;

    // Platform doesn't store any pricing information, so we have to hardcode it
    onReady?.({
      pricesBySubscriptionType: {
        [SubscriptionType.Monthly]: '$2.99',
        [SubscriptionType.Annual]: '$29.99',
      },
    });

    const subscriptionType = url.searchParams.get(
      'subscriptionType',
    ) as SubscriptionType;
    if (url.searchParams.get('paymentStatus') === 'success') {
      this.approvePayment(subscriptionType);
    } else if (url.searchParams.get('paymentStatus') === 'canceled') {
      this.raiseError(new Error('The user canceled the order.'));
    }

    return this;
  }

  async approvePayment(
    subscriptionType: SubscriptionType,
    metadata?: Record<string, any>,
  ): Promise<void> {
    this.onPaymentApproved?.(subscriptionType, metadata);
    this.verifyPayment(subscriptionType, metadata);
  }
  async verifyPayment(
    subscriptionType: SubscriptionType,
    metadata?: Record<string, any>,
  ): Promise<void> {
    const userApi = new UserApi();
    try {
      const userAuthorizationInfo = await userApi.getUserAuthorizationInfo();

      if (userAuthorizationInfo.canAccessStudentUI) {
        this.onPaymentVerified?.(subscriptionType, metadata);
        this.finalizePayment(subscriptionType, metadata);
      } else {
        // Schedule the next ping
        setTimeout(() => this.verifyPayment(subscriptionType, metadata), 1000);
      }
    } catch (error) {
      this.onError?.(error);
    }
  }
  async finalizePayment(
    subscriptionType: SubscriptionType,
    metadata?: Record<string, any>,
  ): Promise<void> {
    this.onPaymentFinished?.(subscriptionType, metadata);
  }

  async raiseError(error: Error): Promise<void> {
    this.onError?.(error);
  }

  async order(
    subscriptionType: SubscriptionType,
    userId: string,
  ): Promise<void> {
    console.log('Ordering subscription for user', userId);

    const paymentApi = new PaymentApi();
    const { redirectUrl } = await paymentApi.createCheckoutSession({
      voucherType: VOUCHER_TYPES_BY_SUBSCRIPTION_TYPE[subscriptionType],
      successUrl: new URL(
        `/unlock-access?paymentStatus=success&subscriptionType=${subscriptionType}`,
        window.location.origin,
      ).toString(),
      cancelUrl: new URL(
        `/unlock-access?paymentStatus=canceled&subscriptionType=${subscriptionType}`,
        window.location.origin,
      ).toString(),
    });

    window.location.href = redirectUrl;
  }
}
